/**
 * The landing page.
 *
 * Renders once from bundled copy, then patches in whatever the server has to
 * say — the Discord count, the ticker row, the real invite and app URLs. That
 * order is the point: the page is readable before any request completes, and a
 * dead API costs it live numbers rather than a blank screen.
 */

import './style.css';
import { h, fill, $, groupInt } from './dom.js';
import { TICKER_TONES, normaliseLanguage } from '@shared/landing-content.js';
import { PRICES, PRICE_OPTIONS, PLAN_LIMITS, SERVER_AI_MONTHLY_ROWS, PLANS_LAUNCH, PLAN_CHANGES_FROM, formatPrice } from '@shared/plans.js';
import { priceRegion, rememberRegion } from '@shared/price-region.js';
import { fetchConfig, fetchContent, fetchDiscord, fetchStats, contentSync, fallbackConfig } from './api.js';
import { phone, desktopMock } from './preview.js';
import { linkResultBanner } from './waitlist.js';
import { revealOnScroll, pinnedSection, highlightOnScroll, previewSwitcher } from './motion.js';
import { livePreviews } from './live.js';

/*
 * The phone's screen for each of the pinned panels, in order: the month at a
 * glance, the payoff queue, then the investments. The live preview follows
 * the panel as the page scrolls through them.
 */
const PANEL_TABS = ['overview', 'debt', 'invest'];

/*
 * The headline, a word at a time. Words rather than letters: the stagger reads
 * as the sentence arriving rather than as an effect, and a word never breaks
 * across lines. The spaces stay text between the words, because a space at
 * the end of an inline-block collapses and would glue them together.
 */
const words = (text, from = 0) => String(text).split(/\s+/).filter(Boolean).flatMap((word, i) => [
  i ? ' ' : null,
  h('span', { class: 'word', style: `--i:${from + i}` }, word)
]).filter(Boolean);
const wordCount = text => String(text).split(/\s+/).filter(Boolean).length;

/*
 * The same key the tracker uses. Somebody who set the app to English should not
 * be greeted in Polish by the page that links to it — the two are one site.
 */
const LANG_KEY = 'lr_portfolio_lang';

/** The six feature-card glyphs, in the design's order. */
const GLYPHS = [
  ['↗', 'var(--accent)', '86%'],
  ['≡', 'var(--accent-lo)', '72%'],
  ['↓', 'var(--danger)', '64%'],
  ['◆', 'var(--accent-hi)', '78%'],
  ['◎', 'var(--accent)', '69%'],
  ['⌘', 'var(--muted)', '92%']
];

function storedLanguage() {
  try {
    const stored = window.localStorage.getItem(LANG_KEY);
    if (stored === 'EN' || stored === 'PL') return stored;
  } catch { /* private mode; fall through to the browser's preference */ }
  return navigator.language?.toLowerCase().startsWith('pl') ? 'PL' : 'EN';
}

/*
 * The page has two addresses: / in Polish and /en/ in English (the build
 * writes en/index.html with English titles and link previews -- see
 * vite.config.js). An address that names its language is a stronger signal
 * than anything stored: a link shared as /en/ opens in English for everyone.
 */
const EN_PATH = '/en/';
const isEnglishPath = () => /^\/en\/?$/.test(window.location.pathname);

function pathLanguage() {
  return isEnglishPath() ? 'EN' : null;
}

/* Switching language moves between the two addresses, so a copied link says what was on screen. */
function followLanguageInAddress(code) {
  const path = window.location.pathname;
  if (path !== '/' && !isEnglishPath()) return;
  const next = code === 'EN' ? EN_PATH : '/';
  if (path === next) return;
  window.history.replaceState({}, '', next + window.location.search + window.location.hash);
}

function storeLanguage(code) {
  try { window.localStorage.setItem(LANG_KEY, code); } catch { /* nothing to do */ }
}

/**
 * Reads and erases the `?wl=` a confirmation or unsubscribe link left behind,
 * so a refresh does not re-announce "signed up" and the state cannot be shared
 * by copying the address bar.
 */
function takeLinkResult() {
  const params = new URLSearchParams(window.location.search);
  const state = params.get('wl');
  const lang = params.get('lang');
  if (!state && !lang) return { state: null, lang: null };

  params.delete('wl');
  params.delete('lang');
  const query = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (query ? '?' + query : '') + window.location.hash);
  return { state, lang };
}

// --------------------------------------------------------------- sections ---

const discordGlyph = () => h('span', { class: 'glyph' }, 'D');

function nav(state) {
  const langToggle = h('div', {
    class: 'lang',
    role: 'button',
    tabIndex: 0,
    title: state.t.langTitle,
    'aria-label': state.t.langTitle,
    onclick: () => state.setLanguage(state.language === 'PL' ? 'EN' : 'PL'),
    onkeydown: event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        state.setLanguage(state.language === 'PL' ? 'EN' : 'PL');
      }
    }
  },
    ['PL', 'EN'].map(code =>
      h('span', { class: 'lang__opt' + (code === state.language ? ' lang__opt--on' : '') }, code)
    )
  );

  return h('nav', { class: 'nav' },
    h('img', { class: 'nav__mark', src: '/logo-mark.png', alt: '' }),
    h('span', { class: 'nav__word' }, 'LIQUIDITY RAIDERS'),
    h('span', { class: 'nav__spacer' }),
    /*
     * The guides were a footer link, at the bottom of a long page -- found by
     * nobody who was not already looking. In the bar they are one glance away
     * on every screen wide enough to hold them.
     */
    h('a', { class: 'nav__link', href: state.t.guidesHref, title: state.t.navGuides }, state.t.navGuidesShort),
    langToggle,
    h('a', { class: 'pill', href: state.config.appUrl },
      h('span', { class: 'pill__dot' }),
      state.t.navTracker
    ),
    h('a', { class: 'btn-discord', href: state.config.discordInviteUrl, rel: 'noopener', target: '_blank' },
      discordGlyph(),
      state.t.navDiscord
    )
  );
}

function hero(state) {
  if (state.t.heroFocus === 'product') return productHero(state);
  return h('header', { class: 'hero' },
    h('div', { class: 'hero__glow' }),
    h('div', { class: 'hero__grid' }),
    h('div', { class: 'hero__lockup', 'data-reveal': '1' },
      h('img', { class: 'logo-drift', src: '/logo-mark.png', alt: 'Liquidity Raiders' }),
      h('div', { class: 'hero__word' }, 'LIQUIDITY RAIDERS')
    ),
    h('h1', { class: 'hero__title', 'data-reveal': '2' },
      words(state.t.h1a), h('br'), h('em', {}, words(state.t.h1b, wordCount(state.t.h1a)))
    ),
    h('p', { class: 'hero__sub', 'data-reveal': '3' }, state.t.heroSub),
    /* The tools, said once, right above the buttons that lead to them. */
    h('p', { class: 'hero__sub hero__sub--tools', 'data-reveal': '4' }, state.t.heroSub2),
    h('div', { class: 'hero__cta', 'data-reveal': '5' },
      h('a', { class: 'btn-lg', href: state.config.discordInviteUrl, rel: 'noopener', target: '_blank' },
        discordGlyph(), state.t.ctaJoin
      ),
      h('a', { class: 'btn-ghost', href: state.config.appUrl }, state.t.ctaOpenTracker)
    )
  );
}

/**
 * The product first: what Portfolio Tracker is, the demo one click away, an
 * account next to it, and the community that builds it a line below. For the
 * English page, where people arrive from Product Hunt and search looking for a
 * finance app -- the community hero above opened on a Discord group, and its
 * big button was "Join the Discord".
 */
function productHero(state) {
  const t = state.t;
  const demoUrl = state.config.appUrl + (state.config.appUrl.includes('?') ? '&' : '?') + 'demo=1';
  return h('header', { class: 'hero hero--product' },
    h('div', { class: 'hero__glow' }),
    h('div', { class: 'hero__grid' }),
    h('div', { class: 'hero__lockup', 'data-reveal': '1' },
      h('img', { class: 'logo-drift', src: '/logo-mark.png', alt: '' }),
      h('div', { class: 'hero__word' }, t.phKicker)
    ),
    h('h1', { class: 'hero__title', 'data-reveal': '2' },
      words(t.phA), h('br'), h('em', {}, words(t.phB, wordCount(t.phA)))
    ),
    h('p', { class: 'hero__sub', 'data-reveal': '3' }, t.phSub),
    h('p', { class: 'hero__sub hero__sub--tools', 'data-reveal': '4' }, t.phSub2),
    h('div', { class: 'hero__cta', 'data-reveal': '5' },
      h('a', { class: 'btn-lg', href: demoUrl }, t.ctaDemo),
      h('a', { class: 'btn-ghost', href: state.config.appUrl }, t.ctaSignUp)
    ),
    h('a', {
      class: 'hero__community', 'data-reveal': '5',
      href: state.config.discordInviteUrl, rel: 'noopener', target: '_blank'
    }, discordGlyph(), t.ctaDiscordLine)
  );
}

/**
 * The marquee.
 *
 * The row is rendered twice and the keyframe travels -50%, which is what makes
 * the loop seamless: at the halfway point the second copy sits exactly where
 * the first started.
 */
function ticker(state) {
  const items = state.ticker.map(item =>
    h('div', { class: 'ticker__item' },
      h('span', { class: 'ticker__dot', style: `background:${state.tones[item.tone] || TICKER_TONES.muted}` }),
      h('span', { class: 'ticker__label' }, item.label),
      h('span', { class: 'ticker__value', style: `color:${state.tones[item.tone] || TICKER_TONES.muted}` }, item.value)
    )
  );
  const row = h('div', { class: 'ticker__row', 'data-ticker': '1' }, items, items.map(el => el.cloneNode(true)));
  return h('div', { class: 'ticker', 'aria-hidden': 'true' }, row);
}

function product(state) {
  const panels = state.t.panels.map((panel, i) =>
    h('div', { class: 'pin__panel' + (i === 0 ? ' is-on' : ''), 'data-panel': String(i) },
      h('div', { class: 'pin__title' }, panel.title),
      h('div', { class: 'pin__body' }, panel.body),
      h('div', { class: 'tags' }, panel.tags.map(tag => h('span', { class: 'tag' }, tag)))
    )
  );

  return h('section', { class: 'pin', id: 'product', 'data-pin': '1' },
    h('div', { class: 'pin__stage' },
      /*
       * A heading over the whole showcase.
       *
       * The stage is sticky and full height with its two columns centred, so
       * most of a screen sat empty above them with nothing saying what was
       * being shown. The three panels introduce themselves one at a time and
       * none of them introduces the section — this does, and because the stage
       * is pinned it stays put while they rotate underneath.
       */
      h('div', { class: 'pin__head', 'data-reveal': '1' },
        h('h2', { class: 'pin__heading' }, state.t.productHeading)
      ),
      h('div', { class: 'pin__grid' },
        h('div', { class: 'pin__copy' },
          h('div', { class: 'kicker' }, state.t.productKicker),
          h('div', { class: 'pin__panels' }, panels),
          h('div', { class: 'pin__dots', role: 'tablist', 'aria-label': state.t.productKicker },
            state.t.panels.map((panel, i) =>
              h('button', {
                class: 'pin__dot' + (i === 0 ? ' is-on' : ''),
                type: 'button',
                'data-dot': String(i),
                role: 'tab',
                'aria-label': panel.title,
                'aria-selected': i === 0 ? 'true' : 'false',
                /*
                 * The dots scroll rather than switch. The panel shown is a pure
                 * function of scroll position, so setting it directly would be
                 * undone by the next frame — moving the page is the only way to
                 * change it that the pinned section will agree with.
                 */
                onclick: () => {
                  const wrap = $('[data-pin]');
                  if (!wrap) return;
                  const span = wrap.offsetHeight - window.innerHeight;
                  const top = wrap.offsetTop + span * ((i + 0.5) / state.t.panels.length);
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              })
            )
          )
        ),
        h('div', { class: 'pin__device' },
          h('div', { class: 'badge-live' },
            h('span', { class: 'dot-live' }),
            state.t.livePreview
          ),
          h('div', { class: 'phone-fit', 'data-phone-fit': '1' },
            phone({ content: state.t, ticker: state.ticker })
          )
        )
      )
    )
  );
}

function features(state) {
  return h('section', { class: 'section section--hairline', id: 'features' },
    h('div', { class: 'wrap stack' },
      h('div', { class: 'head', 'data-reveal': '1' },
        h('div', { class: 'kicker' }, state.t.featuresKicker),
        h('div', { class: 'head__title' }, state.t.featuresTitle)
      ),
      h('div', { class: 'cards' },
        state.t.features.map((feature, i) => {
          const [glyph, colour, bar] = GLYPHS[i] || GLYPHS[0];
          return h('article', { class: 'card', 'data-reveal': String((i % 3) + 1) },
            h('div', { class: 'card__glyph', style: `color:${colour}` }, glyph),
            h('h3', { class: 'card__title' }, feature.title),
            h('p', { class: 'card__body' }, feature.body),
            h('div', { class: 'card__spacer' }),
            h('div', { class: 'card__meter' },
              h('div', { class: 'card__fill', style: `width:${bar};background:${colour}` })
            )
          );
        })
      )
    )
  );
}

/**
 * The guides, as cards of their own under the feature list.
 *
 * Each answers a question the tracker also answers -- which debt to overpay,
 * what the car really burns -- so they sit right after the features that do
 * the same sums, and each card says what it opens and how long it takes.
 */
function guides(state) {
  const t = state.t.guides;
  return h('section', { class: 'section section--hairline', id: 'guides' },
    h('div', { class: 'wrap stack' },
      h('div', { class: 'guides__head' },
        h('div', { class: 'head', 'data-reveal': '1' },
          h('div', { class: 'kicker' }, t.kicker),
          h('div', { class: 'head__title' }, t.title),
          h('p', { class: 'guides__body' }, t.body)
        ),
        h('a', { class: 'btn-ghost guides__all', href: state.t.guidesHref, 'data-reveal': '2' }, t.all)
      ),
      h('div', { class: 'guides__grid' },
        t.items.map((item, i) =>
          /* The complete guide leads, across the full width of the row. */
          h('a', { class: item.main ? 'guide guide--main' : 'guide', href: item.href, 'data-reveal': String(i + 1) },
            h('div', { class: 'guide__meta' }, item.meta),
            h('h3', { class: 'guide__title' }, item.title),
            h('p', { class: 'guide__body' }, item.body),
            h('div', { class: 'guide__spacer' }),
            h('div', { class: 'guide__read' }, t.read + ' →')
          )
        )
      )
    )
  );
}

function showcase(state) {
  /*
   * The address in the fake browser chrome is the tracker's real one. It was a
   * hardcoded string in the design; showing anything other than where the
   * button actually goes would be a small lie on a page about honest numbers.
   */
  let host = state.config.appUrl;
  try {
    const url = new URL(state.config.appUrl);
    host = (url.host + url.pathname).replace(/\/$/, '');
  } catch { /* not absolute; show it as-is rather than inventing a domain */ }

  return h('section', { class: 'showcase' },
    h('div', { class: 'wrap', style: 'display:flex;flex-direction:column;gap:38px' },
      h('div', { class: 'showcase__head', 'data-reveal': '1' },
        h('div', { class: 'showcase__left' },
          h('div', { class: 'kicker' }, state.t.desktopKicker),
          h('div', { class: 'showcase__title' }, state.t.desktopTitle)
        ),
        h('div', { class: 'showcase__right', 'data-highlight': '1' }, state.t.desktopBody)
      ),
      h('div', { class: 'shot', 'data-reveal': '2', 'data-live-host': '1' },
        desktopMock({ content: state.t, ticker: state.ticker, appHost: host }),
        /*
         * The screens of the live preview, one press away. It walks through
         * them on its own while in view, and stops for good once somebody
         * picks one -- the choice is theirs from then on.
         */
        h('div', { class: 'switcher', 'data-switcher': '1' },
          Object.entries(state.t.previewTabs).map(([tab, label], i) =>
            h('button', {
              class: 'switcher__tab' + (i === 0 ? ' is-on' : ''),
              type: 'button',
              'data-preview-tab': tab,
              'aria-pressed': i === 0 ? 'true' : 'false'
            }, label)
          )
        ),
        h('div', { class: 'switcher__note' }, state.t.previewNote)
      )
    )
  );
}

/** `{name}` placeholders in the copy, filled from the values given. */
const fillIn = (text, values) => String(text).replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);

/**
 * Free beside Liquidity+, row by row.
 *
 * Every figure comes from shared/plans.js -- the numbers the app enforces and
 * Stripe charges -- so the table cannot advertise a price or a limit the
 * product does not have. Before the launch it says so: until then nothing is
 * limited, and a table of limits with no date would read as already in force.
 */
function pricing(state) {
  const t = state.t.pricing;
  if (!t) return null;

  const locale = state.language === 'PL' ? 'pl-PL' : 'en-GB';
  const number = value => new Intl.NumberFormat(locale).format(value);
  /* The visitor's region -- the time zone's, or the one picked below; the app sells the same. */
  const where = PRICES[state.priceRegion?.region]?.[state.priceRegion?.currency] ? state.priceRegion : { region: 'eu', currency: 'PLN' };
  const price = PRICES[where.region][where.currency];
  const money = amount => formatPrice(amount, where.currency, state.language);
  const values = {
    month: money(price.month),
    year: money(price.year),
    zero: money(0),
    journal: number(PLAN_LIMITS.free.journalPerMonth),
    aiRows: number(SERVER_AI_MONTHLY_ROWS)
  };

  const launch = PLANS_LAUNCH ? new Date(PLANS_LAUNCH) : null;
  let launchNote = t.soon;
  if (launch) {
    launchNote = launch > new Date()
      ? fillIn(t.startsOn, { date: launch.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) })
      : null;
  }
  /* The October 2026 change to the free plan, said until its day comes. The day as written: Warsaw's. */
  const changes = PLAN_CHANGES_FROM ? new Date(PLAN_CHANGES_FROM) : null;
  if (!launchNote && t.changesOn && changes && changes > new Date()) {
    launchNote = fillIn(t.changesOn, {
      date: changes.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Warsaw' })
    });
  }

  const mark = value => {
    if (value === true) return h('span', { class: 'plans__yes', role: 'img', 'aria-label': t.yes }, '✓');
    if (value === false) return h('span', { class: 'plans__no', role: 'img', 'aria-label': t.no }, '—');
    return h('span', { class: 'plans__val' }, fillIn(value, values));
  };
  /* The same four lines in both columns, top-aligned, so each meets its neighbour. */
  const head = (plan, quiet) => [
    h('div', { class: 'plans__name' }, plan.name),
    h('div', { class: 'plans__price' },
      fillIn(plan.price, values),
      plan.unit && h('span', { class: 'plans__unit' }, plan.unit)
    ),
    h('div', { class: 'plans__sub' }, fillIn(plan.sub, values)),
    h('div', { class: quiet ? 'plans__badge plans__badge--quiet' : 'plans__badge' }, plan.badge)
  ];

  return h('section', { class: 'section section--hairline plans', id: 'plans' },
    h('div', { class: 'wrap stack' },
      h('div', { class: 'head', 'data-reveal': '1' },
        h('div', { class: 'kicker' }, t.kicker),
        h('h2', { class: 'head__title' }, t.title),
        h('p', { class: 'guides__body' }, t.body)
      ),
      h('div', { class: 'plans__table', role: 'table', 'aria-label': t.kicker, 'data-reveal': '2' },
        h('div', { class: 'plans__row', role: 'row' },
          h('div', { class: 'plans__cell plans__corner', role: 'columnheader' }, t.feature),
          h('div', { class: 'plans__cell plans__cell--free plans__head', role: 'columnheader' }, ...head(t.free, true)),
          h('div', { class: 'plans__cell plans__cell--plus plans__head', role: 'columnheader' }, ...head(t.plus, false))
        ),
        t.rows.map(([label, free, plus]) =>
          h('div', { class: 'plans__row', role: 'row' },
            h('div', { class: 'plans__cell plans__label', role: 'rowheader' }, label),
            h('div', { class: 'plans__cell plans__cell--free', role: 'cell' }, mark(free)),
            h('div', { class: 'plans__cell plans__cell--plus', role: 'cell' }, mark(plus))
          )
        ),
        h('div', { class: 'plans__row plans__row--cta', role: 'row' },
          h('div', { class: 'plans__cell', role: 'cell' }),
          h('div', { class: 'plans__cell plans__cell--free', role: 'cell' },
            h('a', { class: 'btn-ghost plans__btn', href: state.config.appUrl }, t.free.cta)),
          h('div', { class: 'plans__cell plans__cell--plus', role: 'cell' },
            h('a', { class: 'btn-lg plans__btn', href: state.config.appUrl }, t.plus.cta))
        )
      ),
      h('div', { class: 'plans__notes', 'data-reveal': '3' },
        t.regions && h('label', { class: 'plans__note plans__region' },
          t.regionLabel + ' ',
          h('select', {
            class: 'plans__select',
            'aria-label': t.regionLabel,
            onchange: event => {
              const [region, currency] = event.target.value.split(':');
              rememberRegion(region, currency);
              state.priceRegion = { region, currency, picked: true };
              /* render() rebuilds the page; keep the reader where they were, at the table. */
              const y = window.scrollY;
              render();
              window.scrollTo({ top: y, behavior: 'instant' });
              document.querySelector('.plans__select')?.focus({ preventScroll: true });
            }
          }, PRICE_OPTIONS.map(({ region, currency }) => h('option', {
            value: region + ':' + currency,
            selected: region === where.region && currency === where.currency
          }, t.regions[region + ':' + currency])))),
        launchNote && h('p', { class: 'plans__note' }, launchNote),
        h('p', { class: 'plans__note' }, t.fine, ' ', h('a', { href: t.termsHref }, t.termsLink + ' →'))
      )
    )
  );
}

/**
 * What a link from a notification-list e-mail says when it lands here.
 *
 * The sign-up section is gone -- the page no longer collects addresses -- but
 * the people already on the list still hold confirmation and unsubscribe links,
 * and following one has to end in a sentence saying what happened. So this
 * appears only then, with the result and nothing to fill in.
 */
function listLinkResult(state) {
  const t = state.t.waitlist;
  const banner = linkResultBanner(state.linkResult, { ...t, errLinkDead: t.linkDead });
  if (!banner) return null;

  return h('section', { class: 'wait', id: 'list' },
    h('div', { class: 'wrap' },
      h('div', { class: 'wait__inner', 'data-reveal': '1' },
        h('div', { class: 'kicker' }, t.kicker),
        banner
      )
    )
  );
}

function bottomCta(state) {
  /*
   * Rendered hidden and revealed once Discord answers.
   *
   * The design shipped a hardcoded "2 480 RAIDERS ONLINE", which is a claim
   * about a real community — so the page must not make it before it has been
   * told the number. An empty slot for a moment beats a confident invention.
   */
  const online = h('div', { class: 'cta__online', 'data-online': '1', hidden: true },
    h('span', { class: 'dot-live' }),
    h('span', { 'data-online-text': '1' })
  );

  return h('section', { class: 'cta' },
    h('div', { class: 'cta__glow' }),
    h('div', { class: 'cta__inner' },
      /*
       * The drifting logo from the top of the page, closing it. The reveal sits
       * on a wrapper: both it and the drift move the element with a transform,
       * and on one element the animation would simply win.
       */
      h('div', { class: 'cta__mark', 'data-reveal': '1' },
        h('img', { class: 'logo-drift cta__logo', src: '/logo-mark.png', alt: '', 'aria-hidden': 'true' })
      ),
      h('p', { class: 'cta__body', 'data-reveal': '3' }, state.t.bottomBody),
      h('a', {
        class: 'cta__btn',
        'data-reveal': '4',
        href: state.config.discordInviteUrl,
        rel: 'noopener',
        target: '_blank'
      }, discordGlyph(), state.t.bottomBtn),
      h('div', { 'data-reveal': '5' }, online)
    )
  );
}

function footer(state) {
  return h('footer', { class: 'foot' },
    h('div', { class: 'foot__inner' },
      h('img', { class: 'foot__mark', src: '/logo-mark.png', alt: '' }),
      h('span', { class: 'foot__word' }, 'LIQUIDITY RAIDERS'),
      h('span', { class: 'foot__spacer' }),
      h('a', { class: 'foot__note', href: state.t.guidesHref }, state.t.navGuides),
      h('a', { class: 'foot__note', href: state.t.legal.termsHref }, state.t.legal.terms),
      h('a', { class: 'foot__note', href: state.t.legal.privacyHref }, state.t.legal.privacy),
      h('a', { class: 'foot__note', href: state.config.appUrl }, state.t.navTracker),
      h('span', { class: 'foot__note' }, state.t.footerNote),
      h('span', { class: 'foot__version' }, __APP_VERSION__)
    )
  );
}

// ------------------------------------------------------------------ boot ---

const page = $('#page');

const state = {
  language: 'PL',
  t: null,
  ticker: [],
  tones: TICKER_TONES,
  linkResult: null,
  config: fallbackConfig(),
  discord: null,
  setLanguage: null,
  /* Which prices the pricing table shows: { region, currency } -- see shared/price-region.js. */
  priceRegion: priceRegion()
};

let teardown = [];

function render() {
  teardown.forEach(fn => fn());
  teardown = [];

  document.documentElement.lang = state.language === 'PL' ? 'pl' : 'en';
  const skip = $('.skip');
  if (skip) skip.textContent = state.t.skipToContent;
  /* The tab and a shared link follow the language on screen, not the Polish the HTML ships with. */
  if (state.t.docTitle) document.title = state.t.docTitle;
  const description = document.querySelector('meta[name="description"]');
  if (description && state.t.docDescription) description.setAttribute('content', state.t.docDescription);

  fill(page,
    nav(state),
    h('main', { id: 'main' },
      hero(state),
      ticker(state),
      product(state),
      features(state),
      guides(state),
      showcase(state),
      pricing(state),
      listLinkResult(state),
      bottomCta(state)
    ),
    footer(state)
  );

  teardown.push(revealOnScroll(page));
  const live = livePreviews(page, {
    appUrl: state.config.appUrl, lang: state.language, title: state.t.previewFrame
  });
  teardown.push(live.teardown);
  teardown.push(pinnedSection(page, {
    panelCount: state.t.panels.length,
    onPanel: index => live.show('mobile', PANEL_TABS[index] || 'overview')
  }));
  teardown.push(previewSwitcher(page, { onPick: tab => live.show('desktop', tab) }));
  teardown.push(highlightOnScroll(page));
  paintDiscord();
}

/**
 * Writes the headcount into the CTA without re-rendering the page around it.
 *
 * `countMode` decides which figure is shown: `members` is everybody who has
 * joined, `online` is who is connected right now. They read differently enough
 * to need different words — "50 RAIDERS" and "16 RAIDERS ONLINE" are both true
 * and only one of them is a claim about this minute.
 */
function paintDiscord() {
  const wrapper = page.querySelector('[data-online]');
  const label = page.querySelector('[data-online-text]');
  if (!wrapper || !label) return;

  const stats = state.discord;
  const mode = stats?.countMode === 'online' ? 'online' : 'members';
  // Falls back to the other figure rather than to nothing: if the invite gave a
  // member count but no presence, showing the member count is still honest.
  const count = stats ? (stats[mode] ?? stats.online ?? stats.members) : null;

  if (count === null || count === undefined) {
    // Unknown, or Discord could not be reached and no fallback was configured.
    wrapper.hidden = true;
    return;
  }

  const suffix = mode === 'online' || stats[mode] === null || stats[mode] === undefined
    ? state.t.onlineSuffix
    : state.t.membersSuffix;

  label.textContent = `${groupInt(count)} ${suffix}`;
  wrapper.hidden = false;

  /*
   * Only a genuinely live reading is shown at full strength. The server answers
   * with a configured fallback when Discord is unreachable, and that number
   * should not carry the same confidence as one Discord just gave us.
   */
  wrapper.setAttribute('data-pending', stats.live ? '0' : '1');
}

async function loadLanguage(language, { firstRun = false } = {}) {
  state.language = language;
  state.t = contentSync(language);
  if (firstRun) render();

  /*
   * Content and stats are both language-scoped, so they are refetched on a
   * switch; the Discord count is not, and is fetched once at boot.
   */
  const [content, stats] = await Promise.all([
    fetchContent(language),
    fetchStats(language)
  ]);

  state.t = content;
  state.ticker = stats.ticker;
  state.tones = stats.tones;
  render();
}

async function boot() {
  const link = takeLinkResult();
  state.linkResult = link.state;

  // A confirmation link carries the language the person signed up in, which is
  // a better guess than anything stored in *this* browser — they may be opening
  // the link on a different device entirely.
  const language = link.lang ? normaliseLanguage(link.lang) : pathLanguage() || storedLanguage();
  followLanguageInAddress(language);

  state.setLanguage = next => {
    if (next === state.language) return;
    storeLanguage(next);
    followLanguageInAddress(next);
    loadLanguage(next);
  };

  // Render immediately from the bundle, then let the server fill in the rest.
  state.t = contentSync(language);
  state.language = language;
  const initial = await fetchStats(language);
  state.ticker = initial.ticker;
  state.tones = initial.tones;
  render();

  state.config = await fetchConfig();
  render();

  state.discord = await fetchDiscord();
  if (state.discord.inviteUrl && state.discord.inviteUrl !== state.config.discordInviteUrl) {
    /*
     * The invite Discord just confirmed outranks the configured one, which can
     * have been revoked since somebody typed it into the env file. Only worth a
     * re-render when it actually differs — `render()` rebuilds the whole page.
     */
    state.config = { ...state.config, discordInviteUrl: state.discord.inviteUrl };
    render();
  } else {
    paintDiscord();
  }

  // The stats endpoint may have been slow at boot; a late refresh is free.
  const late = await fetchStats(state.language);
  if (late.live) {
    state.ticker = late.ticker;
    state.tones = late.tones;
    render();
  }
}

boot();
